// Route guards: authentication, account-enabled, and capability gating.
import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useMe } from '../api/hooks'
import { ApiError } from '../api/client'
import { can } from '../rbac/roles'
import type { Capability } from '../rbac/roles'
import { useMeCtx } from './MeContext'
import { useEntitlements } from '../entitlements/useEntitlements'
import type { EntitlementFlag } from '../entitlements/types'
import { useIsLoggedIn } from './useIsLoggedIn'
import LoginRedirect from '../pages/LoginRedirect'

export function RequireAuth({ children }: { children: ReactNode }) {
  // Entra ODER E-Mail-OTP (V1.14): ein OTP-angemeldeter Nutzer wird nicht zum
  // Entra-Redirect geschickt.
  const isAuth = useIsLoggedIn()
  return isAuth ? <>{children}</> : <LoginRedirect />
}

// The backend stamps a stable machine-readable "code" on its 403 ProblemDetails
// so the SPA can tell "awaiting admin approval" apart from "account rejected".
function problemCode(body: string): string | null {
  try {
    return (JSON.parse(body) as { code?: string }).code ?? null
  } catch {
    return null
  }
}

export function RequireEnabled({ children, fallback }: { children: ReactNode; fallback: ReactNode }) {
  const { isLoading, error } = useMe()
  if (isLoading) return <>{fallback}</>
  if (error) {
    // #172: a 401 means the session is dead (token expired/rejected), NOT "account not
    // allowed". healUnauthorized() is already driving a fresh interactive login — show
    // the calm fallback instead of dead-ending at the terminal /denied page.
    if (error instanceof ApiError && error.status === 401) return <>{fallback}</>
    // Only "account_pending" means the user is provisioned but awaiting approval.
    // A domain/tenant rejection, an unknown 403, or any other hard error is NOT a
    // pending account — show "access denied" instead of a false "awaiting approval".
    if (error instanceof ApiError && error.status === 403) {
      const code = problemCode(error.body)
      if (code === 'account_pending') return <Navigate to="/pending" replace />
      // L7: blocked by an admin — a deliberate rejection with its own page, not "awaiting".
      if (code === 'account_disabled') return <Navigate to="/blocked" replace />
    }
    return <Navigate to="/denied" replace />
  }
  return <>{children}</>
}

export function RequireCap({ cap, children }: { cap: Capability; children: ReactNode }) {
  const { role } = useMeCtx()
  return can(role, cap) ? <>{children}</> : <Navigate to="/" replace />
}

// V1.14: Lizenz-Gate für ganze Routen. Flag aus ⇒ kein harter Abbruch, sondern
// Zugang ausblenden (Umleitung auf Start). Der Einstieg ins UI wird parallel
// ausgeblendet; dieser Guard schützt zusätzlich einen direkten Deep-Link.
export function RequireFlag({ flag, children }: { flag: EntitlementFlag; children: ReactNode }) {
  const { isEnabled } = useEntitlements()
  return isEnabled(flag) ? <>{children}</> : <Navigate to="/" replace />
}
